import { type ReactNode, useEffect } from 'react'
import { useLocation } from 'wouter'
import { motion } from 'motion/react'
import { tgBack, haptic } from '@/lib/tg'

/**
 * Sub-page wrapper for nested routes inside the main tabs.
 * - Header with back button + title
 * - Slide-in animation from the right
 * - Telegram BackButton wired automatically
 */
export function SubPage({
  title,
  subtitle,
  back = '/profile',
  children,
}: {
  title: string
  subtitle?: string
  back?: string
  children: ReactNode
}) {
  const [, setLocation] = useLocation()

  useEffect(() => {
    const goBack = () => {
      haptic('light')
      setLocation(back)
    }
    return tgBack(goBack)
  }, [back, setLocation])

  return (
    <motion.div
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -24 }}
      transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
      className="flex-1 flex flex-col min-h-0 bg-[var(--color-bg)]"
    >
      <header className="px-5 pt-4 pb-3">
        <button
          onClick={() => {
            haptic('light')
            setLocation(back)
          }}
          className="tap size-8 border bg-[var(--color-surface)] flex items-center justify-center"
          style={{ borderColor: 'var(--color-line)', borderRadius: 4 }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 6l-6 6 6 6" />
          </svg>
        </button>
        <div className="rule w-10 mt-3.5" />
        <h1 className="display text-[34px] leading-[0.88] mt-2 truncate">{title}</h1>
        {subtitle && <div className="label mt-2 truncate">{subtitle}</div>}
      </header>
      <div className="flex-1 min-h-0 overflow-y-auto pb-8">{children}</div>
    </motion.div>
  )
}
